import React, { useState, useMemo, useEffect } from 'react';
import { Autocomplete, TextField, CircularProgress, InputAdornment } from '@mui/material';
import { LocationOn as LocationIcon } from '@mui/icons-material';
import { debounce } from 'lodash';
import axios from 'axios';

interface NominatimResult {
    place_id: number;
    licence: string;
    osm_type: string;
    osm_id: number;
    boundingbox: string[];
    lat: string;
    lon: string;
    display_name: string;
    class: string;
    type: string;
    importance: number;
    address: {
        house_number?: string;
        road?: string;
        suburb?: string;
        city?: string;
        town?: string;
        village?: string;
        county?: string;
        state?: string;
        postcode?: string;
        country?: string;
        country_code?: string;
        [key: string]: string | undefined;
    };
}

interface NominatimAutocompleteProps {
    onLocationSelect: (location: {
        address: string;
        lat: number;
        lng: number;
        city?: string;
        country_code?: string;
        postal_code?: string;
        street?: string;
        street_number?: string;
    }) => void;
    placeholder?: string;
    label?: string;
}

const NominatimAutocomplete: React.FC<NominatimAutocompleteProps> = ({
    onLocationSelect,
    placeholder = "Search for a location...",
    label = "Location"
}) => {
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState<NominatimResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [value, setValue] = useState<NominatimResult | null>(null);

    const fetchResults = useMemo(
        () =>
            debounce(async (request: { input: string }, callback: (results?: NominatimResult[]) => void) => {
                try {
                    const response = await axios.get<NominatimResult[]>('https://nominatim.openstreetmap.org/search', {
                        params: {
                            q: request.input,
                            format: 'json',
                            addressdetails: 1,
                            limit: 5,
                        },
                    });
                    callback(response.data);
                } catch (error) {
                    console.error('Error fetching Nominatim results:', error);
                    callback([]);
                }
            }, 500),
        [],
    );

    useEffect(() => {
        let active = true;

        if (inputValue === '') {
            setOptions(value ? [value] : []);
            return undefined;
        }

        setLoading(true);

        fetchResults({ input: inputValue }, (results?: NominatimResult[]) => {
            if (active) {
                let newOptions: NominatimResult[] = [];

                if (value) {
                    newOptions = [value];
                }

                if (results) {
                    newOptions = [...newOptions, ...results];
                }

                setOptions(newOptions);
                setLoading(false);
            }
        });

        return () => {
            active = false;
        };
    }, [value, inputValue, fetchResults]);

    const handleSelect = (event: any, newValue: NominatimResult | null) => {
        setValue(newValue);

        if (newValue) {
            const { address, lat, lon, display_name } = newValue;

            // Prioritize County for 'city' field as per user request context (Kenyan structure)
            // But fallback to city/town/village if county is missing or for general usage
            const city = address.county || address.city || address.town || address.village || address.suburb;

            const location = {
                address: display_name,
                lat: parseFloat(lat),
                lng: parseFloat(lon),
                city: city,
                country_code: address.country_code?.toUpperCase(),
                postal_code: address.postcode,
                street: address.road,
                street_number: address.house_number,
            };

            onLocationSelect(location);
        }
    };

    return (
        <Autocomplete
            id="nominatim-autocomplete"
            getOptionLabel={(option) => option.display_name}
            filterOptions={(x) => x}
            options={options}
            autoComplete
            includeInputInList
            filterSelectedOptions
            value={value}
            noOptionsText="No locations found"
            onChange={handleSelect}
            onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    fullWidth
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <InputAdornment position="start">
                                <LocationIcon color="primary" />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <React.Fragment>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
            renderOption={(props, option) => {
                return (
                    <li {...props}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <LocationIcon sx={{ color: 'text.secondary', mr: 2 }} />
                            <div>
                                {option.display_name}
                            </div>
                        </div>
                    </li>
                );
            }}
        />
    );
};

export default NominatimAutocomplete;
